/* =====================================================================
 * SEOHead — 라우트별 동적 <head> 메타태그 (title, description, OG, Twitter)
 *
 * react-helmet-async를 사용하여 각 라우트에 맞는 SEO 메타를 주입합니다.
 * routeConfig의 선언적 SEO 데이터를 기반으로 자동 적용됩니다.
 * ===================================================================== */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getRouteByPath,
  PUBLIC_ROUTE_SEO,
  formatSEOTitle,
  APP_NAME,
  SITE_URL,
  DEFAULT_OG_IMAGE,
  type RouteSEO,
} from './routeConfig';

interface SEOHeadProps {
  title?: string;
  description?: string;
  ogImage?: string;
  /** Canonical path when this URL duplicates another page (e.g. `/login` → `/`). */
  canonicalPath?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({ title, description, ogImage, canonicalPath }) => {
  const { t } = useTranslation(['common', 'workspace']);
  const { pathname } = useLocation();

  let seo: RouteSEO | undefined;

  const workspaceRoute = getRouteByPath(pathname);
  if (workspaceRoute) {
    seo = workspaceRoute.seo;
  } else {
    seo = PUBLIC_ROUTE_SEO[pathname];
  }

  const pageTitle = title ?? (seo ? t(seo.titleKey) : undefined);
  const pageDesc = description ?? (seo ? t(seo.descriptionKey) : '');
  const fullTitle = formatSEOTitle(pageTitle);
  const ogTitle = seo?.ogTitle ?? fullTitle;
  const ogDesc = seo?.ogDescription ?? pageDesc;
  const resolvedOgImage = ogImage ?? DEFAULT_OG_IMAGE;
  const canonicalUrl = SITE_URL + (canonicalPath ?? pathname);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {pageDesc && <meta name="description" content={pageDesc} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={ogTitle} />
      {ogDesc && <meta property="og:description" content={ogDesc} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={APP_NAME} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={resolvedOgImage} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      {ogDesc && <meta name="twitter:description" content={ogDesc} />}
      <meta name="twitter:image" content={resolvedOgImage} />
    </Helmet>
  );
};
